import React from 'react';
import { FileSpreadsheet, TrendingUp, Settings, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface QuickExportWidgetProps {
  onExportToday: (type: 'fraudulent' | 'all') => void;
  onExportWeek: (type: 'fraudulent' | 'all') => void;
  onCustomExport: () => void;
  todayStats: {
    fraudulent: number;
    total: number;
  };
  weekStats: {
    fraudulent: number;
    total: number;
  };
  recentExports: Array<{
    filename: string;
    size: string;
    date: Date;
    url: string;
  }>;
}

export function QuickExportWidget({ 
  onExportToday,
  onExportWeek, 
  onCustomExport,
  todayStats,
  weekStats,
  recentExports 
}: QuickExportWidgetProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Download className="h-5 w-5" />
          Quick Export
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Quick Export Buttons */}
        <div className="grid grid-cols-1 gap-3">
          <Button 
            variant="outline" 
            className="justify-start h-auto p-4"
            onClick={() => onExportToday('fraudulent')}
          >
            <div className="flex items-center gap-3 w-full">
              <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-lg">
                <FileSpreadsheet className="h-4 w-4 text-red-600 dark:text-red-400" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-medium">Today's Fraud</div>
                <div className="text-sm text-muted-foreground">
                  {todayStats.fraudulent} fraudulent leads
                </div>
              </div>
              <Badge variant="destructive" className="ml-auto">
                {todayStats.fraudulent}
              </Badge>
            </div>
          </Button>
          
          <Button 
            variant="outline" 
            className="justify-start h-auto p-4"
            onClick={() => onExportWeek('all')}
          >
            <div className="flex items-center gap-3 w-full">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <TrendingUp className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-medium">This Week</div>
                <div className="text-sm text-muted-foreground">
                  {weekStats.total} total leads
                </div>
              </div>
              <Badge variant="secondary" className="ml-auto">
                {weekStats.total}
              </Badge>
            </div>
          </Button>
          
          <Button 
            variant="outline" 
            className="justify-start h-auto p-4"
            onClick={onCustomExport}
          >
            <div className="flex items-center gap-3 w-full">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                <Settings className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-medium">Custom Export</div>
                <div className="text-sm text-muted-foreground">
                  Configure filters and options
                </div>
              </div>
            </div>
          </Button>
        </div>
        
        {/* Recent Exports */}
        {recentExports.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-muted-foreground">Recent Exports</h4>
            <div className="space-y-2">
              {recentExports.slice(0, 3).map((exportFile, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <div className="flex-1 min-w-0">
                    <a 
                      href={exportFile.url}
                      className="text-primary hover:underline truncate block"
                      download
                    >
                      {exportFile.filename}
                    </a>
                    <div className="text-xs text-muted-foreground">
                      {exportFile.size} • {exportFile.date.toLocaleDateString()}
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" asChild>
                    <a href={exportFile.url} download>
                      <Download className="h-3 w-3" />
                    </a>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Export Statistics */}
        <div className="pt-3 border-t space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Today's Fraud Rate:</span>
            <span className="font-medium">
              {todayStats.total > 0 
                ? `${Math.round((todayStats.fraudulent / todayStats.total) * 100)}%`
                : '0%'
              }
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Weekly Fraud Rate:</span>
            <span className="font-medium">
              {weekStats.total > 0 
                ? `${Math.round((weekStats.fraudulent / weekStats.total) * 100)}%`
                : '0%'
              }
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}